<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <div slot="header">
          <b-row>
            <b-col cols="6">
              <span>{{ 'order_update_title' | translate('global') }}</span>
            </b-col>
          </b-row>
        </div>
        <div v-if="spinners.contentIsLoading" class="text-center">
          <b-spinner type="grow" variant="primary"/>
        </div>
        <div v-else>
          <b-card border-variant="success" bg-variant="light" class="text-center">
            <blockquote class="card-blockquote">
              <table class="table table-sm center">
                <thead>
                  <tr>
                    <th class="no-border-top">{{ 'id' | translate('global') }}</th>
                    <th class="no-border-top">{{ 'user' | translate('global') }}</th>
                    <th class="no-border-top">{{ 'order_created_at' | translate('global') }}</th>
                    <th class="no-border-top">{{ 'status' | translate('global') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="border-r-3">{{ info.id }}</td>
                    <td class="border-r-3">{{ info.user }}</td>
                    <td class="border-r-3">{{ info.created_at }}</td>
                    <td class="border-r-3">{{ info.status }}</td>
                  </tr>
                </tbody>
              </table>
            </blockquote>
          </b-card>

          <b-row>
            <b-col cols="12">
              <b-alert :show="monthBudgetExceeded" variant="danger" class="text-center">
                {{ 'month_budget_exceeded_alert' | translate('global') }}
                <strong>{{ monthBudgetExceeded }}</strong> EUR
              </b-alert>
            </b-col>
            <b-col cols="12">
              <b-nav tabs>
                <b-nav-item :to="{ name: 'OrderUpdate', params: { id: $route.params.id, tab: 'purchase-application' }}"
                              :active="activeTab === 'purchase-application'">
                  {{ 'purchase_application_tab' | translate('global') }}
                </b-nav-item>
                <b-nav-item :to="{ name: 'OrderUpdate', params: { id: $route.params.id, tab: 'purchase-order' }}"
                              :active="activeTab === 'purchase-order'">
                  {{ 'purchase_order_tab' | translate('global') }}
                </b-nav-item>
                <b-nav-item :to="{ name: 'OrderUpdate', params: { id: $route.params.id, tab: 'fact-invoice' }}"
                              :active="activeTab === 'fact-invoice'">
                  {{ 'fact_and_invoice_tab' | translate('global') }}
                </b-nav-item>
                <b-nav-item :to="{ name: 'OrderUpdate', params: { id: $route.params.id, tab: 'advance-invoice' }}"
                              :active="activeTab === 'advance-invoice'">
                  {{ 'advance_invoice_tab' | translate('global') }}
                </b-nav-item>
                <b-nav-item :to="{ name: 'OrderUpdate', params: { id: $route.params.id, tab: 'attachments' }}"
                            :active="activeTab === 'attachments'">
                  {{ 'attachments_tab' | translate('global') }}
                </b-nav-item>
                <b-nav-item :to="{ name: 'OrderUpdate', params: { id: $route.params.id, tab: 'history' }}"
                            :active="activeTab === 'history'">
                  {{ 'history_tab' | translate('global') }}
                </b-nav-item>
              </b-nav>

              <div class="tab-content">
                <PurchaseApplication v-if="activeTab === 'purchase-application'" @getData="getData"/>
                <PurchaseOrder v-if="activeTab === 'purchase-order'" @getData="getData"/>
                <FactAndInvoice v-if="activeTab === 'fact-invoice'" @getData="getData"/>
                <AdvanceInvoice v-if="activeTab === 'advance-invoice'" @getData="getData"/>
                <Attachments v-if="activeTab === 'attachments'" @getData="getData"/>
                <History v-if="activeTab === 'history'" :page="page"/>
              </div>
            </b-col>
          </b-row>
        </div>
        <div slot="footer" class="flex-container">
          <Approve/>
          <Reject class="ml-2"/>
          <Delete class="ml-2"/>
          <b-button :to="{ name: 'Orders' }" type="reset" variant="secondary" class="ml-2">
            {{ 'back_to_list' | translate('global') }}
          </b-button>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import statuses from '@/store/statuses';
import VuexTypes from '@/store/types/index';
import { mapGetters, mapActions } from 'vuex';
import PurchaseApplication from './Tabs/PurchaseApplication.vue';
import PurchaseOrder from './Tabs/PurchaseOrder/PurchaseOrder.vue';
import FactAndInvoice from './Tabs/FactAndInvoice/FactAndInvoice.vue';
import AdvanceInvoice from './Tabs/AdvanceInvoice/AdvanceInvoice.vue';
import Attachments from './Tabs/Attachments/Attachments.vue';
import History from './Tabs/History.vue';
import Reject from './OrderReject.vue';
import Approve from './OrderApprove.vue';
import Delete from './OrderDelete.vue';

const OrdersTypes = VuexTypes.orders;

export default {
  mounted() {
    this.getData();
    this.getClassifiers();
    if (typeof (this.$route.params.tab) === 'undefined') {
      this.$router.push({
        name: 'OrderUpdate',
        params: { id: this.$route.params.id, tab: 'purchase-application' }
      });
    }
  },
  props: ['tab', 'page'],
  data() {
    return {
      spinners: {
        contentIsLoading: true,
        isApproving: false
      },
      monthBudgetExceeded: false,
      activeTab: this.$route.params.tab,
      info: {
        user: '',
        created_at: '',
        status: '',
      }
    };
  },
  components: {
    PurchaseApplication,
    PurchaseOrder,
    FactAndInvoice,
    AdvanceInvoice,
    Attachments,
    History,
    Reject,
    Approve,
    Delete
  },
  computed: {
    ...mapGetters('Orders', [
      'orderUpdateData',
      'orderApproval',
      'orderDelete',
      'orderUpdateAttachmentsStatus'
    ]),
  },
  watch: {
    '$route'(to, from) {
      this.changeTab();

      if (from.params.id !== to.params.id) {
        this.getData();
      }
    },
    orderUpdateData: {
      immediate: true,
      handler: function () {
        if (this.orderUpdateData.status === statuses.LOADED) {
          this.spinners.contentIsLoading = false;
          this.fillData();
        }
      },
      deep: true
    },
    orderApproval: {
      handler: function () {
        if (this.orderApproval.status === statuses.LOADED) {
          this.alertSuccess();
          this.getData();
        } else if (this.orderApproval.status === statuses.ERROR) {
          this.alertError();
        }
      },
      deep: true
    },
    orderDelete: {
      handler: function () {
        if (this.orderDelete.status === statuses.LOADED) {
          this.alertSuccess();
        } else if (this.orderDelete.status === statuses.ERROR) {
          this.alertError();
        }
      },
      deep: true
    },
    orderUpdateAttachmentsStatus: {
      handler: function () {
        if (this.orderUpdateAttachmentsStatus.status === statuses.LOADED) {
          this.getData();
        }
      },
      deep: true
    },
  },
  methods: {
    ...mapActions('Orders', [
      OrdersTypes.ORDER_UPDATE_DATA,
      OrdersTypes.ORDERS_CLASSIFIERS,
    ]),
    getData() {
      this.ORDER_UPDATE_DATA(this.$route.params.id);
    },
    getClassifiers() {
      this.ORDERS_CLASSIFIERS();
    },
    fillData() {
      const order = this.orderUpdateData.data.order ? this.orderUpdateData.data.order : {};

      this.info = {
        id: order.id,
        user: order.user ? order.user.name : '',
        created_at: order.created_at,
        status: this.$t(order.status, 'statuses'),
      };

      this.monthBudgetExceeded = false;
      const availableBudget = this.orderUpdateData.data['additional']
        ? Number(this.orderUpdateData.data['additional']['curent_month_budget']['available']) : null;
      const spent = this.orderUpdateData.data['additional']
        ? Number(this.orderUpdateData.data['additional']['curent_month_budget']['spent']) : null;
      const itemSum = this.orderUpdateData.data['order']
        ? Number(this.orderUpdateData.data['order']['total_sum']) : null;

      if (availableBudget) {
        if (order.status !== 'ORDER_REQUEST_APPROVED') {
          this.monthBudgetExceeded = (itemSum + spent) - availableBudget;
        } else {
          this.monthBudgetExceeded = spent - availableBudget;
        }
      }
    },
    changeTab() {
      const tab = this.tab;
      if (tab === 'purchase-application' || tab === 'purchase-order' || tab === 'fact-invoice'
        || tab === 'advance-invoice' || tab === 'attachments' || tab === 'history') {
        this.activeTab = tab;
      } else {
        this.$router.push({ name: 'OrderUpdate', params: { id: this.$route.params.id, tab: 'purchase-application' } });
      }
    },
  }
};
</script>
